import { useState, useEffect, useMemo, useCallback } from 'react';
import { useRestaurant } from '@/lib/session';
import { fetchFeedback, type Feedback } from '@/lib/data';

export default function FeedbackAdminPage() {
  const restaurant = useRestaurant();
  const [items, setItems] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(0); // 0 = all, else star count
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await fetchFeedback(restaurant.id)); }
    finally { setLoading(false); }
  }, [restaurant.id]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = items.length;
    const avg = total ? items.reduce((s, f) => s + f.rating, 0) / total : 0;
    const dist = [5, 4, 3, 2, 1].map((star) => ({
      star,
      count: items.filter((f) => f.rating === star).length,
    }));
    return { total, avg, dist };
  }, [items]);

  const shown = filter ? items.filter((f) => f.rating === filter) : items;
  const link = `${window.location.origin}/feedback/${restaurant.slug}?src=link`;

  const copyLink = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const fmt = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground-900">Customer Feedback</h1>
          <p className="text-sm text-foreground-500">Ratings &amp; comments from your QR / link feedback form.</p>
        </div>
        <button onClick={copyLink} className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-links-line mr-1.5"></i>{copied ? 'Copied!' : 'Copy feedback link'}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-background-50 border border-background-200 rounded-lg p-5">
          <p className="text-xs text-foreground-500 uppercase font-semibold">Average rating</p>
          <div className="flex items-end gap-2 mt-1">
            <span className="text-3xl font-bold text-foreground-900">{stats.avg.toFixed(1)}</span>
            <span className="text-amber-400 text-lg mb-1">
              {'★'.repeat(Math.round(stats.avg))}<span className="text-background-300">{'★'.repeat(5 - Math.round(stats.avg))}</span>
            </span>
          </div>
          <p className="text-xs text-foreground-500 mt-1">{stats.total} responses</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-5 sm:col-span-2">
          <p className="text-xs text-foreground-500 uppercase font-semibold mb-2">Distribution</p>
          {stats.dist.map((d) => (
            <div key={d.star} className="flex items-center gap-2 text-sm mb-1">
              <span className="w-8 text-foreground-600">{d.star}★</span>
              <div className="flex-1 h-2 rounded-full bg-background-100 overflow-hidden">
                <div className="h-full bg-amber-400" style={{ width: `${stats.total ? (d.count / stats.total) * 100 : 0}%` }}></div>
              </div>
              <span className="w-8 text-right text-foreground-500">{d.count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        {[0, 5, 4, 3, 2, 1].map((n) => (
          <button key={n} onClick={() => setFilter(n)}
            className={`px-3 py-1.5 text-sm rounded-lg cursor-pointer transition-colors ${filter === n ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}>
            {n === 0 ? 'All' : `${n}★`}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-foreground-500">Loading…</p>
      ) : shown.length === 0 ? (
        <div className="text-center py-16 text-foreground-400">
          <i className="ri-chat-smile-2-line text-4xl"></i>
          <p className="mt-2 text-sm">No feedback yet. Share your feedback link or print a QR for tables.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((f) => (
            <div key={f.id} className="bg-background-50 border border-background-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-amber-400">{'★'.repeat(f.rating)}<span className="text-background-300">{'★'.repeat(5 - f.rating)}</span></span>
                <span className="text-xs text-foreground-400">{fmt(f.createdAt)}</span>
              </div>
              {f.comment && <p className="text-sm text-foreground-700 mt-2">{f.comment}</p>}
              <div className="text-xs text-foreground-500 mt-2">
                {f.customerName || 'Anonymous'}{f.phone ? ` · ${f.phone}` : ''} · via {f.source}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
